import { useOutletContext } from "react-router-dom";

import { OutletContext } from "@oneleet/utils";
import { logout } from "@oneleet/utils/src/logout";

import { ArrowRight, LogOut } from "lucide-react";

import { Button, InlineCode, NgButton } from "@oneleet/components";

export function FullPageError({
  status,
  title,
  description,
  showLogoutButton = true,
  sentryErrorId,
}: {
  status?: string;
  title?: string;
  description?: string;
  showLogoutButton?: boolean;
  sentryErrorId?: string;
}) {
  // Outlet context will be null if the error occurs in the root route
  const { appId } = useOutletContext<OutletContext>() ?? {};

  return (
    <div className="relative flex h-full flex-col">
      <title>{title ?? "Error"}</title>
      {showLogoutButton && appId && (
        <div className="absolute top-0 z-50 flex h-16 w-full shrink-0 items-center gap-x-4 bg-[#fafafa] px-4 sm:gap-x-6 sm:px-6 lg:px-8">
          <div className="flex-right z-50 flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1 flex-row items-center justify-end gap-x-4 lg:gap-x-6">
              <Button
                label="Logout"
                variant="plain"
                color="gray"
                startIcon={<LogOut />}
                onClick={() => logout({ appId })}
              />
            </div>
          </div>
        </div>
      )}
      <main className="grid h-full place-items-center bg-[#fafafa] px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center">
          <p className="text-lg font-semibold text-red-600">
            {status || "Error"}
          </p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {title || "An error occurred."}
          </h1>
          <p className="mt-6 text-base leading-7 text-gray-600">
            {description ? (
              description
            ) : sentryErrorId ? (
              <span className="flex flex-col items-center gap-y-2">
                Send us a message on Slack with the error ID below and
                we&apos;ll get this fixed as soon as possible.
                <InlineCode>{sentryErrorId}</InlineCode>
              </span>
            ) : (
              <span className="flex flex-col items-center gap-y-2">
                Send us a message on Slack and we&apos;ll get this fixed as soon
                as possible.
              </span>
            )}
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <NgButton
              variant="solid"
              color="accent"
              size="large"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </NgButton>
            <a href="mailto:support@oneleet.com">
              <NgButton
                variant="plain"
                color="neutral"
                size="large"
                endIcon={<ArrowRight />}
              >
                Contact us
              </NgButton>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
